import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { parsePhotoIds, type AnswerValue, type QuestionGroupField, type SingleQuestionDefinition } from "@ferrivia/api-contract";
import { useQuestionnaireCopy } from "./questionCopy";

/* Renders a stored answer back as a plain string — the read-back half of the
 * questionnaire, which apps/client never needed (it only ever rendered
 * editable controls) and apps/clinic's Screener tab does.
 *
 * This helper formats; it never decides. Every branch below dispatches on
 * `value.kind` — the wire shape's own discriminant — never on what the
 * value is. There is no score, band, threshold, flag, colour or derived
 * ordering here (root CLAUDE.md, "the software does not interpret"); a date
 * is formatted, not judged as early or late, a number is printed with its
 * unit, not compared to a range. See test/answerText.test.tsx's purity
 * assertion.
 *
 * An unrecognised choice/multi_choice option id falls back to the raw id,
 * the same render-it-as-itself rule apps/clinic's entryContent.ts follows
 * for timeline enums — a later catalog change must not blank out an older
 * answer.
 *
 * `forGroupField` needs both the group key and the field to look up copy
 * (`groupFieldFor(groupKey, fieldKey)` — see questionCopy.ts), so it takes
 * one more argument than `forQuestion`; `QuestionGroupField` alone carries no
 * reference back to the group it lives in (see RepeatingGroup.tsx, which
 * threads `groupKey` the same way).
 */

export interface AnswerText {
  forQuestion: (question: SingleQuestionDefinition, value: AnswerValue) => string;
  forGroupField: (groupKey: string, field: QuestionGroupField, value: AnswerValue) => string;
}

export function useAnswerText(): AnswerText {
  const { t } = useTranslation("questionnaire");
  const { tag } = useFormatters();
  const copy = useQuestionnaireCopy();

  // "YYYY-MM-DD" values carry no time component, so they are formatted in
  // UTC — the same reasoning as JourneyPane.formatDay: parsing as local time
  // would show a browser west of Finland the day before.
  function formatDate(value: string): string {
    return new Intl.DateTimeFormat(tag, {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${value}T00:00:00Z`));
  }

  function formatBoolean(value: boolean): string {
    return value ? t("answer.yes", "Yes") : t("answer.no", "No");
  }

  function formatNumber(value: number, unit: string | undefined): string {
    const formatted = new Intl.NumberFormat(tag).format(value);
    return unit ? `${formatted} ${unit}` : formatted;
  }

  function textFor(
    value: AnswerValue,
    unit: string | undefined,
    optionLabelFor: (id: string) => string | undefined,
  ): string {
    switch (value.kind) {
      case "text":
        return value.value;
      case "number":
        return formatNumber(value.value, unit);
      case "boolean":
        return formatBoolean(value.value);
      case "date":
        return formatDate(value.value);
      case "choice":
        return optionLabelFor(value.value) ?? value.value;
      case "multi_choice":
        return value.value.map((id) => optionLabelFor(id) ?? id).join(", ");
    }
  }

  return {
    forQuestion: (question, value) =>
      textFor(value, copy.questionFor(question.key)?.unit, (id) => copy.optionLabelFor(question.key, id)),
    // A photo field's value is photo ids, which mean nothing to a reader:
    // render how many there are, never the ids.
    forGroupField: (groupKey, field, value) =>
      field.control === "photo_upload" && value.kind === "text"
        ? t("answer.photoCount", "{{count}} kuvaa", { count: parsePhotoIds(value.value).length })
        : textFor(value, copy.groupFieldFor(groupKey, field.key)?.unit, (id) =>
        copy.groupFieldOptionLabelFor(groupKey, field.key, id),
      ),
  };
}
